import React from 'react';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/form/FormField';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateBudget, useDeleteBudget, useUpdateBudget } from '@/services/budgetService';
import { queryKeys } from '@/services/queryClient';
import { describeApiError } from '@/utils/apiError';
import type { BudgetReportRow } from '@/types';

const MAX_AMOUNT = 999_999_999_999.99;

/** Mirrors the API's SaveDepartmentBudgetValidator */
const budgetSchema = z.object({
  amount: z
    .number({ message: 'Enter an amount' })
    .min(0, 'Enter an amount of 0 or more')
    .max(MAX_AMOUNT, 'The amount is too large')
    .refine((value) => Math.round(value * 100) / 100 === value, 'Use at most two decimal places'),
  notes: z.string().max(1000, 'Notes can be up to 1000 characters'),
});

interface BudgetEditDialogProps {
  /** The department row being edited; null closes the dialog */
  row: BudgetReportRow | null;
  fiscalYear: number;
  fiscalYearLabel: string;
  onClose: () => void;
}

/**
 * Set, change or delete a department's budget for one fiscal year (#19). Creates the budget when
 * the row has none, replaces it otherwise; a duplicate year from a concurrent save comes back as
 * the API's 409 message.
 */
export const BudgetEditDialog: React.FC<BudgetEditDialogProps> = ({ row, fiscalYear, fiscalYearLabel, onClose }) => {
  const [amount, setAmount] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState<{ amount?: string; notes?: string }>({});
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const createBudget = useCreateBudget();
  const updateBudget = useUpdateBudget();
  const deleteBudget = useDeleteBudget();
  const queryClient = useQueryClient();

  // Load the row's values each time the dialog opens for a (different) row
  const [loadedFor, setLoadedFor] = React.useState<string | null>(null);
  const rowKey = row ? `${row.departmentId}:${row.budgetId ?? 'new'}` : null;
  if (rowKey !== loadedFor) {
    setLoadedFor(rowKey);
    setAmount(row?.budget !== undefined ? String(row.budget) : '');
    setNotes(row?.budgetNotes ?? '');
    setFieldErrors({});
    setServerError(null);
  }

  const isEdit = !!row?.budgetId;
  const saving = createBudget.isPending || updateBudget.isPending;

  const handleSave = async () => {
    if (!row?.departmentId) return;
    const parsed = budgetSchema.safeParse({ amount: amount.trim() === '' ? undefined : Number(amount), notes });
    if (!parsed.success) {
      const errors: { amount?: string; notes?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as 'amount' | 'notes';
        errors[key] ??= issue.message;
      }
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setServerError(null);
    const data = { fiscalYear, amount: parsed.data.amount, notes: parsed.data.notes.trim() || undefined };
    try {
      if (isEdit) {
        await updateBudget.mutateAsync({ departmentId: row.departmentId, id: row.budgetId!, data });
      } else {
        await createBudget.mutateAsync({ departmentId: row.departmentId, data });
      }
      onClose();
    } catch (err) {
      setServerError(describeApiError(err));
      // Another Manager saved this year first (409) or removed the budget (404): reload the report
      // so the row shows what is really there and a retry does not repeat the same error
      const status = (err as { statusCode?: number })?.statusCode;
      if (status === 409 || status === 404) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      }
    }
  };

  const handleDelete = async () => {
    if (!row?.departmentId || !row.budgetId) return;
    try {
      await deleteBudget.mutateAsync({ departmentId: row.departmentId, id: row.budgetId });
      setConfirmDelete(false);
      onClose();
    } catch (err) {
      setConfirmDelete(false);
      if ((err as { statusCode?: number })?.statusCode === 404) {
        // Another Manager deleted it first: the goal is met, so reload the report and close
        void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
        onClose();
        return;
      }
      setServerError(describeApiError(err));
    }
  };

  return (
    <>
      <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEdit ? 'Edit budget' : 'Set budget'}: {row?.departmentName}
            </DialogTitle>
            <DialogDescription>Fiscal year {fiscalYearLabel}</DialogDescription>
          </DialogHeader>
          {/* noValidate: the schema reports errors inline instead of the browser's min/step tooltips */}
          <form
            noValidate
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSave();
            }}
          >
            {serverError && (
              <Alert variant="destructive">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}
            <FormField label="Amount" required error={fieldErrors.amount}>
              <Input
                id="budget-amount"
                aria-label="Amount"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                aria-invalid={!!fieldErrors.amount}
              />
            </FormField>
            <FormField label="Notes" error={fieldErrors.notes} helperText="Optional, up to 1000 characters">
              <Textarea
                aria-label="Notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                aria-invalid={!!fieldErrors.notes}
              />
            </FormField>
            <DialogFooter className="gap-2 sm:justify-between">
              {isEdit ? (
                <Button type="button" variant="outline" className="text-destructive" onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this budget?</AlertDialogTitle>
            <AlertDialogDescription>
              {row?.departmentName} will have no budget for fiscal year {fiscalYearLabel}. Contracts and costs are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDelete()}>Delete budget</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
